import Icon from "@components/Atoms/Icon";
import t from "@ext/localization/locale/translate";
import PropertiesScrollContainer from "@ext/properties/components/Helpers/PropertiesScrollContainer";
import PropertyItem from "@ext/properties/components/Helpers/PropertyItem";
import { usePropertyEditor } from "@ext/properties/logic/hooks/usePropertyEditor";
import type { Property, PropertyValue } from "@ext/properties/models";
import { isComplexProperty } from "@ext/templates/models/properties";
import { DropdownMenuItem, DropdownMenuSeparator } from "@ui-kit/Dropdown";
import { useCallback, useMemo } from "react";

interface AddPropertyProps {
	properties: Property[] | PropertyValue[];
	catalogProperties: Map<string, Property>;
	canAdd?: boolean;
	canEdit?: boolean;
	disabled?: boolean;
	onSubmit: (propertyName: string, value?: string) => void;
	onDelete?: (propertyName: string) => void;
}

const AddProperty = (props: AddPropertyProps) => {
	const { canAdd = false, canEdit = true, properties, catalogProperties, onSubmit, onDelete, disabled } = props;
	const editProperty = usePropertyEditor({ canAdd, catalogProperties, properties });

	const changeProperty = useCallback(
		(id: string, value?: string) => {
			onSubmit(id, value);
		},
		[onSubmit],
	);

	const articlePropertyMap = useMemo(() => {
		const map = new Map<string, string[]>();
		for (const p of properties) {
			map.set(p.id, p.value);
		}
		return map;
	}, [properties]);

	const filteredCatalogProperties = useMemo(() => {
		return Array.from(catalogProperties.values()).filter((property) => !isComplexProperty[property.type]);
	}, [catalogProperties]);

	const items = useMemo(() => {
		return filteredCatalogProperties.map((property) => {
			const hasProperty = articlePropertyMap.has(property.id);
			const articleValue = articlePropertyMap.get(property.id);
			const selected = hasProperty ? (articleValue?.length ? articleValue : true) : false;
			return (
				<PropertyItem
					disabled={disabled}
					key={property.id}
					onClick={changeProperty}
					onDeleteClick={onDelete}
					onEditClick={canEdit ? editProperty : undefined}
					property={{ ...property, value: articleValue }}
					selected={selected}
				/>
			);
		});
	}, [filteredCatalogProperties, disabled, changeProperty, onDelete, canEdit, editProperty, articlePropertyMap]);

	return (
		<>
			<PropertiesScrollContainer>{items}</PropertiesScrollContainer>
			{items.length > 0 && canAdd && <DropdownMenuSeparator />}
			{canAdd && (
				<DropdownMenuItem onSelect={() => editProperty(null)}>
					<Icon code="plus" />
					{t("properties.add")}
				</DropdownMenuItem>
			)}
		</>
	);
};

export default AddProperty;
